import api from './api';

export interface TableLayoutTemplate {
  id: number;
  settings_key: string;
  name: string;
  user_email: string;
  is_shared: boolean;
  /**
   * A "View": an org-wide layout published by a manager, selectable from the
   * View dropdown on Custom Search / Custom Filter. Views and templates live in
   * one table but are listed separately — `list` never returns Views and
   * `listViews` never returns templates.
   */
  is_view: boolean;
  is_mine: boolean;
  /**
   * May the CURRENT user rename/delete this row? Ownership for a template, the
   * save-search-view permission for a View — so the server decides, not
   * `is_mine`.
   */
  can_manage: boolean;
  setting_value: any;
  updated_at: string;
}

class TableTemplatesService {
  /** Own + shared templates; optionally across tables. Always excludes System Views. */
  async list(settingsKey: string, allKeys = false): Promise<TableLayoutTemplate[]> {
    const response = await api.get(`/table-templates/${settingsKey}`, {
      params: { all_keys: allKeys ? 1 : 0 },
    });
    return response.data.templates || [];
  }

  /** Every published System View for one table key, whoever published it. */
  async listViews(settingsKey: string): Promise<TableLayoutTemplate[]> {
    const response = await api.get(`/table-templates/${settingsKey}`, { params: { views: 1 } });
    return response.data.templates || [];
  }

  /**
   * Every published System View, from EVERY table key.
   *
   * What the Load View modal offers in its System Views section, so a manager
   * publishes once and it is reachable from any grid that shows the modal. Each
   * row still carries its own `settings_key`.
   *
   * `anyKey` only fills the path segment — the server ignores it in this mode.
   * It exists because the route is `/table-templates/{settings_key}`, and reusing
   * the already-registered and already-granted route costs no endpoint
   * registration and no grant clone.
   */
  async listAllViews(anyKey: string = 'all'): Promise<TableLayoutTemplate[]> {
    const response = await api.get(`/table-templates/${anyKey}`, {
      params: { views: 1, all_keys: 1 },
    });
    return response.data.templates || [];
  }

  /**
   * Publish (or overwrite) a View. Keyed by name alone, not by owner — a View is
   * one org-wide object. Requires the save-search-view permission; the server
   * 403s otherwise and forces is_shared on.
   */
  async saveView(settingsKey: string, name: string, settingValue: any): Promise<TableLayoutTemplate> {
    const response = await api.put(`/table-templates/${settingsKey}`, {
      name,
      is_view: true,
      is_shared: true,
      setting_value: settingValue,
    });
    return response.data;
  }

  /** Create, or overwrite the caller's template with the same name. */
  async save(settingsKey: string, name: string, isShared: boolean, settingValue: any): Promise<TableLayoutTemplate> {
    const response = await api.put(`/table-templates/${settingsKey}`, {
      name,
      is_shared: isShared,
      setting_value: settingValue,
    });
    return response.data;
  }

  /** Rename and/or toggle sharing on one of the caller's own templates. */
  async update(id: number, patch: { name?: string; is_shared?: boolean }): Promise<TableLayoutTemplate> {
    const response = await api.patch(`/table-templates/${id}`, patch);
    return response.data;
  }

  async remove(id: number): Promise<void> {
    await api.delete(`/table-templates/${id}`);
  }
}

export default new TableTemplatesService();
